import { useEffect } from "react";
import { useParams } from "react-router-dom";
import { LOGO_URL } from "../lib/constants";

/**
 * Permanent redirect for legacy Wix blog URLs.
 *
 *   /post/{slug}   →   https://blog.medstry.in/post/{slug}
 *
 * Server-side 301 isn't possible here (Emergent ingress only routes /api/* to
 * the backend; all other paths serve the React SPA). Best practical SEO-safe
 * client-side redirect:
 *
 *   1. <meta http-equiv="refresh"> for crawlers that don't run JS
 *   2. <link rel="canonical"> pointing at the new URL
 *   3. window.location.replace() for the user-facing redirect
 *
 * For a true 301 with HTTP status, add a Cloudflare Page Rule:
 *   medstry.in/post/*  →  https://blog.medstry.in/post/$1  (status 301)
 */
export default function PostRedirect() {
  const { slug } = useParams();
  const target = `https://blog.medstry.in/post/${slug || ""}`;

  useEffect(() => {
    // Inject crawler-friendly head tags
    const meta = document.createElement("meta");
    meta.httpEquiv = "refresh";
    meta.content = `0; url=${target}`;
    document.head.appendChild(meta);

    const canonical = document.createElement("link");
    canonical.rel = "canonical";
    canonical.href = target;
    document.head.appendChild(canonical);

    // Immediate user redirect (replace = no history entry)
    window.location.replace(target);

    return () => {
      meta.remove();
      canonical.remove();
    };
  }, [target]);

  return (
    <main
      className="min-h-screen flex items-center justify-center bg-slate-50 px-6"
      data-testid="post-redirect"
    >
      <div className="text-center max-w-md">
        <img src={LOGO_URL} alt="Medstry" className="h-12 w-auto object-contain mx-auto mb-6" />
        <h1 className="font-display text-2xl font-bold text-slate-900 mb-3">
          Taking you to the blog post…
        </h1>
        <p className="text-slate-600 text-sm mb-6">
          Our blog has moved to{" "}
          <a href="https://blog.medstry.in" className="text-indigo-600 hover:underline">
            blog.medstry.in
          </a>
        </p>
        <a
          href={target}
          className="inline-flex items-center gap-2 btn-primary px-5 py-3 text-sm font-semibold"
        >
          Continue to post
        </a>
      </div>
    </main>
  );
}
